const modules = import.meta.glob('../page/**/index.vue')
// user-chat/chat 下的页面保持懒加载（chat-uikit-vue3 体积大，不进主包），
// 通过 prefetch 在空闲时预加载，兼顾首屏速度和点击响应速度
const chatModules = import.meta.glob('../page/user-chat/chat/**/index.vue')
// 空闲时预加载 chat 相关 chunk
if (typeof window !== 'undefined') {
  const prefetchChat = () => {
    for (const key in chatModules) {
      chatModules[key]().catch(() => {})
    }
  }
  if (window.requestIdleCallback) {
    window.requestIdleCallback(prefetchChat)
  } else {
    setTimeout(prefetchChat, 1000)
  }
}
import { getMenuListApi } from '@/api/sysadmin'
import { getToken, LocalStore } from '@/util/common'

const MENU_CACHE_KEY = 'menu_cache';
const MENU_CACHE_EXPIRE = 10 * 60 * 1000; // 缓存有效期 10 分钟
const PROMOTION_PAGE = modules['../page/promotion-manage/index.vue'];

const normalizeRoutePath = (value = '') => `/${String(value).trim().replace(/^\/*/, '').replace(/\/*$/, '')}`;

// 推广人管理当前仅在本地开发版本提供；正式环境仍由后端菜单权限控制。
const addPromotionManageDevMenu = (menus) => {
  if (!import.meta.env.DEV || !getToken() || !Array.isArray(menus)) return menus;

  const hasPromotionMenu = (items) => items.some((menu) =>
    ['/promotion-manage', '/promote-manage'].includes(menu?.path?.trim()?.replace(/^\/*/, '/')) ||
    ['/promotion-manage', '/promote-manage'].includes(menu?.component?.trim()?.replace(/^\/*/, '/')) ||
    (Array.isArray(menu?.children) && hasPromotionMenu(menu.children))
  );
  if (hasPromotionMenu(menus)) return menus;

  return [...menus, {
    path: '/promotion-manage',
    permissionCode: 'promotion-manage',
    permissionName: '推广人管理',
    component: '/promotion-manage',
    visible: true,
    children: []
  }];
};

// 组件路径匹配缓存（避免每次都遍历 paths）
const componentCache = new Map();

function resolveComponent(menucomponent) {
  if (!menucomponent) return undefined;
  const normalizedPath = normalizeRoutePath(menucomponent);
  const promotionAliases = new Set([
    '/promote-manage',
    '/promote-manage/promoter-manage',
    '/promote-manage/promoter-manage/index'
  ]);
  const componentPath = promotionAliases.has(normalizedPath) ? '/promotion-manage' : normalizedPath;
  if (componentCache.has(componentPath)) return componentCache.get(componentPath);

  const path1 = `../page${componentPath}/index.vue`;
  const path2 = `../page${componentPath}.vue`;

  let comp = modules[path1];
  if (!comp) comp = modules[path2];

  componentCache.set(componentPath, comp);
  return comp;
}

/**
 * 递归处理菜单数据，转换为路由格式
 * @param {Array} menus - 原始菜单数据
 * @returns {Array} - 转换后的路由数据
 */
const processMenus = (menus, isRoot = true, parentPath = '') => {
  if (!menus || !Array.isArray(menus)) return [];

  const result = new Array(menus.length);
  for (let i = 0; i < menus.length; i++) {
    const menu = menus[i];
    const rawPath = menu.path?.trim() || '';
    const path = isRoot ? `/${rawPath.replace(/^\/*/, '')}` : rawPath.replace(/^\/*/, '');
    const fullPath = isRoot ? path : `${parentPath.replace(/\/*$/, '')}/${path}`;
    const routeItem = {
      path,
      visible: !!menu.visible,
      name: menu.permissionCode?.trim() || menu.permissionName?.trim() || '',
      meta: {
        title: menu.permissionName?.trim() || '',
        layout: true,
        icon: menu.icon || ''
      }
    };

    // 如果有组件路径，尝试匹配组件
    const comp = resolveComponent(menu.component?.trim()) || (
      normalizeRoutePath(fullPath) === '/promote-manage/promoter-manage' ? PROMOTION_PAGE : undefined
    );
    if (comp) {
      routeItem.component = comp;
    }

    // 如果有子菜单，递归处理
    if (menu.children && menu.children.length > 0) {
      const children = processMenus(menu.children, false, fullPath);
      routeItem.children = children;
      if (children.length > 0) {
        const childPath = children[0]?.path || '';
        routeItem.redirect = path && childPath
          ? (path.endsWith('/') ? path : path + '/') + (childPath.startsWith('/') ? childPath.slice(1) : childPath)
          : path;
      }
    }

    result[i] = routeItem;
  }
  return result;
};

/**
 * 从菜单列表中找到第一个有组件的菜单路径（组合父级路径）
 */
const findFirstValidPath = (menus, parentPath = '') => {
  if (!menus || !Array.isArray(menus)) return '';

  for (let i = 0; i < menus.length; i++) {
    const menu = menus[i];
    const childPath = (menu.path || '').replace(/^\/*/, '');
    const separator = parentPath && !parentPath.endsWith('/') ? '/' : '';
    const currentPath = parentPath ? `${parentPath}${separator}${childPath}` : `/${childPath}`;

    if (menu.component) {
      return currentPath;
    }
    if (menu.children && menu.children.length > 0) {
      const childPath = findFirstValidPath(menu.children, currentPath);
      if (childPath) return childPath;
    }
  }
  return '';
};

// 从缓存读取菜单并处理
function getCachedRoutes() {
  try {
    const cache = LocalStore.get(MENU_CACHE_KEY);
    if (!cache || !cache.menus || !cache.timestamp) return null;
    if (Date.now() - cache.timestamp > MENU_CACHE_EXPIRE) return null;
    const processedMenus = processMenus(addPromotionManageDevMenu(cache.menus));
    const firstPath = findFirstValidPath(processedMenus);
    return {
      routes: [
        { path: '/', redirect: firstPath || '/home' },     
        ...processedMenus
      ],
      firstPath
    };
  } catch (e) {
    return null;
  }
}

// 保存菜单到缓存
function saveMenuCache(menus) {
  try {
    LocalStore.set(MENU_CACHE_KEY, {
      menus,
      timestamp: Date.now()
    });
  } catch (e) {
    // ignore
  }
}

// 清除菜单缓存（登出时可调用）
export function clearMenuCache() {
  try {
    LocalStore.remove(MENU_CACHE_KEY);
  } catch (e) {
    // ignore
  }
}

const fetchUserRoute = () => {
  return new Promise((resolve) => {
    if (!getToken()) {
      resolve([]);
      return;
    }

    // 先尝试从缓存读取，快速返回
    const cached = getCachedRoutes();
    if (cached) {
      resolve(cached.routes);
      // 后台静默刷新菜单（不阻塞）
      getMenuListApi().then(res => {
        const menus = res.data?.menus || [];
        saveMenuCache(menus);
      }).catch(() => {});
      return;
    }

    // 无缓存，走接口
    getMenuListApi().then(res => {
      const menus = res.data?.menus || [];
      saveMenuCache(menus);
      const processedMenus = processMenus(addPromotionManageDevMenu(menus));
      const firstPath = findFirstValidPath(processedMenus);

      const routes = [
        { path: '/', redirect: firstPath || '/home' },        
        // {
        //   path: '/system-manage/menu',
        //   name: 'Menu',
        //   meta: { title: '菜单管理', layout: true },
        //   visible: true,
        //   component: modules['../page/system-manage/menu/index.vue']
        // },
        // {
        //   path: '/customer-business-mpportunities/vehicle-maintenance-manage',
        //   name: 'VehicleMaintenanceManage',
        //   meta: { title: '车辆保养管理', layout: true },
        //   visible: true,
        //   component: modules['../page/customer-business-mpportunities/vehicle-maintenance-manage/index.vue']
        // },
        ...processedMenus
      ];
      resolve(routes);
    }).catch(() => {
      resolve([]);
    });
  })
}

export default fetchUserRoute;
